// @flow
import { useLoadBilling } from 'queries/payments/useLoadBilling'
import { useLoadPlans } from 'queries/payments/useLoadPlans'
import * as React from 'react'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import SelectField from 'ui/molecules/SelectField'
import Radio from 'ui/Radio'

type Props = {
  onCancel: () => void,
}

function UpdatePlan({ onCancel }: Props): React.Node {
  const { data: billing, isLoading: isBillingLoading } = useLoadBilling()
  const { data: plansResult, isLoading: isPlansLoading } = useLoadPlans()
  const [billingCycle, setBillingCycle] = React.useState('month')
  const [selectedPlanId, setSelectedPlanId] = React.useState('')

  const isLoading = isBillingLoading && isPlansLoading
  const plansByCycle = React.useMemo(() => {
    return (
      plansResult?.results.filter((plan) => plan.interval === billingCycle) ||
      []
    )
  }, [billingCycle, plansResult?.results])
  const currentPlan = billing?.subscription?.plan

  const options = React.useMemo(() => {
    if (!plansByCycle) return []

    return plansByCycle.map((plan) => ({
      label: `${
        plan.readable_price ||
        plan.product?.name ||
        plan.nickname ||
        plan.name ||
        ''
      } ${plan.id === currentPlan.id ? ' (Current Plan)' : ''}`,
      value: plan.id,
      disabled: plan.id === currentPlan.id,
    }))
  }, [currentPlan?.id, plansByCycle])

  React.useEffect(() => {
    if (currentPlan?.id) {
      setSelectedPlanId(billing.subscription.plan.id)
    }
    if (billing?.subscription?.plan?.interval) {
      setBillingCycle(billing.subscription.plan.interval)
    }
  }, [
    currentPlan?.id,
    billing.subscription.plan.interval,
    billing.subscription.plan.id,
  ])

  const getBillingCycleChecked = React.useCallback(
    (cycle) => {
      return billingCycle === cycle
    },
    [billingCycle]
  )

  return (
    <FlexBox>
      <Space size="large">
        <Text>Update Plan</Text>

        <SelectField
          showPlaceholder
          disabled={isLoading}
          label="Choose Plan"
          onChange={(e) => {
            setSelectedPlanId(e.target.value)
          }}
          options={options}
          value={selectedPlanId}
        />

        <Flex
          $flexDirection="col"
          $verticalSpacing="small"
          key={getBillingCycleChecked('month') ? 'month' : 'year'}
        >
          <Text>Billing Cycle</Text>
          <Flex $horizontalSpacing="medium" align="center">
            <Radio
              checked={getBillingCycleChecked('month')}
              label="Monthly"
              onChange={() => {
                setBillingCycle('month')
                if (
                  (
                    plansResult?.results.filter(
                      (plan) =>
                        plan.id === selectedPlanId && plan.interval === 'month'
                    ) || []
                  ).length === 0
                ) {
                  setSelectedPlanId('')
                }
              }}
            />
            <Radio
              checked={getBillingCycleChecked('year')}
              label="Annually"
              onChange={() => {
                setBillingCycle('year')
                if (
                  (
                    plansResult?.results.filter(
                      (plan) =>
                        plan.id === selectedPlanId && plan.interval === 'year'
                    ) || []
                  ).length === 0
                ) {
                  setSelectedPlanId('')
                }
              }}
            />
          </Flex>
        </Flex>

        <Flex $horizontalSpacing="small" align="center" justify="end">
          <Button
            disabled={isLoading}
            onClick={() => {
              onCancel && onCancel()
            }}
          >
            Cancel
          </Button>
          <Button
            $primary
            disabled={
              selectedPlanId === '' || selectedPlanId === currentPlan.id
            }
            loading={isLoading}
          >
            Save Changes
          </Button>
        </Flex>
      </Space>
    </FlexBox>
  )
}

export default UpdatePlan
